import type { ComponentProps } from "react";
import { Children, isValidElement } from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

export const inputStyles = tv({
  slots: {
    root: [
      "flex w-full cursor-text gap-2 overflow-hidden rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm text-neutral-950 placeholder-neutral-700 transition-colors outline-none hover:border-neutral-500 data-placeholder:text-neutral-700 [&>input]:placeholder:text-neutral-700",

      "focus-within:border-neutral-500 focus-within:ring-2 focus-within:ring-secondary-200 focus-within:ring-offset-1",

      "disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-700 has-disabled:cursor-not-allowed has-disabled:border-neutral-200 has-disabled:bg-neutral-50 has-disabled:text-neutral-700",

      "has-aria-invalid:border-red-700 aria-invalid:border-red-700",

      "has-[[data-slot=input]:read-only]:pointer-events-none has-[[data-slot=input]:read-only]:border-transparent has-[[data-slot=input]:read-only]:bg-secondary-200/20",
    ],
    input: "w-full focus:outline-none",
    slot: "flex items-center gap-2 fill-secondary-400 text-sm text-secondary-400 data-[side=left]:order-first data-[side=right]:order-last [&_svg]:size-4",
  },
});

const { root, input, slot } = inputStyles();

type RootProps = Omit<ComponentProps<"input">, "className"> &
  VariantProps<typeof inputStyles>;

const Root = ({ children, ...props }: RootProps) => {
  const slots = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === Slot,
  );

  const [leftSlot, rightSlot] = slots;

  return (
    <label className={root()}>
      {leftSlot}

      <input data-slot="input" {...props} className={input()} />

      {rightSlot}
    </label>
  );
};

type SlotProps = ComponentProps<"div"> & {
  asChild?: boolean;
  side?: "left" | "right";
};

const Slot = ({ className, asChild, side, ...props }: SlotProps) => {
  const Component = asChild ? SlotPrimitive.Root : "div";

  return (
    <Component
      data-slot="input-slot"
      data-side={side}
      className={slot({ className })}
      {...props}
    />
  );
};

export const Input = {
  Root,
  Slot,
};
